import { Form, Formik } from 'formik'
import { useState } from 'react'

import type { ReactElement } from 'react'
import type ReactPlayer from 'react-player'
import type * as Yup from 'yup'
import type { IFileWithAdditionalData, IVideoCreate, IVideoResultItem, IVideoUpdate } from '~/types/video'

import { MODAL_ID } from './UpdateModal'

import { useVideosCategoriesListQuery, useVideosTagsListQuery } from '../queries'

import Button from '~/components/Buttons/Button'
import FileUploadInput from '~/components/Inputs/FileUploadInput'
import MultiSelectInput from '~/components/Inputs/Formik/MultiSelectInput'
import SelectInput from '~/components/Inputs/Formik/SelectInput'
import TextArea from '~/components/Inputs/Formik/TextArea'
import TextInput from '~/components/Inputs/Formik/TextInput'
import { ModalBody, ModalFooter } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import { reactSelectMenuPositionDef } from '~/utils/options'

interface Props {
  onCancel: () => void
  onSubmit: (data: IVideoSubmit) => void
  onVideoPreviewReady?: (player: ReactPlayer) => void
  type: 'update' | 'create'
  validationSchema: Yup.ObjectSchema<Yup.ObjectShape>
  video?: IVideoResultItem
}

export interface IVideoSubmit {
  data: IVideoCreate | IVideoUpdate
  file?: IFileWithAdditionalData
}

export interface Values {
  category: IOption | null
  description?: string
  file: File | null
  tags: IOption[]
  title: string
}

const VideoForm = ({ onCancel, onSubmit, onVideoPreviewReady, type, validationSchema, video }: Props): ReactElement => {
  const { data: optionTags, isLoading: isOptionTagsLoading } = useVideosTagsListQuery()

  const { data: optionCategories } = useVideosCategoriesListQuery()

  const [duration, setDuration] = useState<number>()

  const getInitialValues = (): Values => {
    if (type === 'update' && video) {
      const { category, description, tags, title } = video
      return {
        category: optionCategories?.find(({ value }) => value === category) || null,
        description,
        file: null,
        tags: optionTags?.filter(({ value }) => tags.find(({ id }) => id === value)) || [],
        title,
      }
    }
    return {
      category: null,
      description: '',
      file: null,
      tags: [],
      title: '',
    }
  }

  const handleSubmit = async (data: Values) => {
    let body: IVideoSubmit

    switch (type) {
      case 'create':
        if (!data.file) {
          throw new Error()
        }
        body = {
          data: {
            category: data.category?.value as string,
            description: data.description,
            duration,
            file_name: data.file.name,
            mime_type: data.file.type,
            size: data.file.size,
            tags: data.tags?.map((el) => el.label),
            title: data.title,
          },
          file: data.file,
        }
        break
      case 'update':
        body = {
          data: {
            category: data.category?.value || '',
            description: data.description,
            id: video?.id as string,
            tags: data.tags?.map((el) => el.label),
            title: data.title,
          },
        }
        break
    }

    await onSubmit(body)
  }

  const handleVideoPreviewReady = (player: ReactPlayer) => {
    setDuration(player.getDuration())
    onVideoPreviewReady && onVideoPreviewReady(player)
  }

  return (
    <div className={`video-modal-container ${type}`}>
      <Formik initialValues={getInitialValues()} onSubmit={handleSubmit} validationSchema={validationSchema}>
        {({ isSubmitting }) => (
          <Form>
            <ModalBody>
              <div className="form-container">
                {type === 'create' && (
                  <div className="video-uploader">
                    <FileUploadInput
                      accept={{ 'video/*': [] }}
                      label="Upload"
                      name="file"
                      onVideoPreviewReady={handleVideoPreviewReady}
                      required
                    />
                  </div>
                )}
                <div className="video-details">
                  <RequiredInformation />
                  <TextInput label="Title" name="title" placeholder="Type a name" required />
                  <SelectInput
                    {...reactSelectMenuPositionDef(MODAL_ID)}
                    label="Category"
                    name="category"
                    options={optionCategories}
                    required
                  />
                  <MultiSelectInput
                    {...reactSelectMenuPositionDef(MODAL_ID)}
                    isLoading={isOptionTagsLoading}
                    label="Tags"
                    name="tags"
                    options={optionTags}
                  />
                  <TextArea label="Description" name="description" placeholder="Type a description" />
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button ghost onClick={onCancel} type="button">
                Cancel
              </Button>
              <Button disabled={isSubmitting} type="submit">
                Save
              </Button>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default VideoForm
